import { action, makeObservable, observable, runInAction } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { AxiosError } from 'axios';

import TableStore from 'ui/stores/TableStore';

import { apiErrorsWithKeys } from 'utils/api/errors';
import { EventType } from 'utils/events/constants';
import { ActionEvent, DeletedUsersPayload } from 'utils/events/types';
import API from 'utils/api';
import { UploadUserType } from 'utils/api/types';
import { EditUserResponse, UserResponse, UsersCreationInBulkParams } from 'utils/api/modules/user/user.types';
import { EMPTY_IMPORT_VOCABULARY, ImportVocabulary } from 'utils/csv/importVocabulary';
import {
  SiteLookupResult,
  createImportRowResolver,
  duplicateImportColumns,
  isBlankImportRow,
  siteNamesInSheet,
} from 'utils/csv/resolveImportRow';

import RootStore from '../Root';

export interface ImportParseContext {
  vocabulary: ImportVocabulary;
  /** Transformed header row, so two columns collapsing onto one field can be reported. */
  fields: string[];
}

/** The error log has to show what was typed, not the uuids the cells resolved to. */
export interface PreparedImportUser {
  payload: UsersCreationInBulkParams;
  source: UploadUserType;
}

export type ImportError = UsersCreationInBulkParams & { error: string; source?: UploadUserType };

const SITE_LOOKUP_LIMIT = 2;

const SITE_LOOKUP_BATCH_SIZE = 5;

export default class UserListStore extends TableStore<RootStore, typeof API, UserResponse, UserResponse> {
  @observable usersToUpload: PreparedImportUser[] = [];

  /** Rows rejected before any request went out; folded into `uploadErrors` when the import runs. */
  @observable resolveErrors: ImportError[] = [];

  @observable uploadErrors: ImportError[] = [];

  @observable importVocabulary: ImportVocabulary = EMPTY_IMPORT_VOCABULARY;

  @observable usersUploadedSuccessfully = 0;

  @observable uploadCount = 0;

  @observable uploadedAllUsers = false;

  @observable usersLoaded = false;

  /**
   * Bumped on every reset and every parse, so a site lookup still in flight can tell its sheet is no longer
   * the loaded one — the caller doesn't await the parse, so a reset or a second file can land mid-lookup.
   */
  private importGeneration = 0;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, api.user.loadUsers);
    this.sortingOrder = 'asc';
    this.sortingColumn = 'name';

    makeObservable(this);
  }

  @subscribe(EventType.DeletedUsers)
  @action
  deletedUsers({ payload }: ActionEvent<DeletedUsersPayload>): void {
    if (this.isFailure || !this.isLoaded) {
      return;
    }

    this.items = this.items.filter((user) => {
      if (payload.users.indexOf(user.uuid) === -1) {
        return true;
      }
      eventBus.unregister(user);
      return false;
    });

    if (this.count !== undefined) {
      this.count -= payload.users.length;
    }
  }

  @subscribe(EventType.UpdatedUser)
  @action
  updatedUser({ payload }: ActionEvent<EditUserResponse>): void {
    if (this.isFailure || !this.isLoaded) {
      return;
    }

    const index = this.items.findIndex((u) => u.uuid === payload.uuid);
    if (index !== -1) {
      this.items[index] = {
        ...this.items[index],
        ...payload,
      };
    }
  }

  @action.bound
  resetUsers(): void {
    this.items.forEach((user) => eventBus.unregister(user));
    this.items = [];
  }

  private async lookUpSite(name: string): Promise<[string, SiteLookupResult]> {
    try {
      const response = await this.api.entities.searchEntity('sites', undefined, {
        exact_query: name,
        limit: SITE_LOOKUP_LIMIT,
      })();
      if (response.data.length === 0) {
        return [name, { error: `Associated site "${name}" was not found.` }];
      }
      if (response.data.length > 1) {
        return [name, { error: `Associated site "${name}" matches more than one site.` }];
      }
      return [name, { uuid: response.data[0].uuid }];
    } catch {
      return [name, { error: `Associated site "${name}" could not be looked up.` }];
    }
  }

  private async lookUpSites(names: string[]): Promise<Map<string, SiteLookupResult>> {
    const pending = [...names];
    const entries: [string, SiteLookupResult][] = [];
    while (pending.length) {
      const batch = pending.splice(0, SITE_LOOKUP_BATCH_SIZE);
      entries.push(...(await Promise.all(batch.map((name) => this.lookUpSite(name)))));
    }
    return new Map(entries);
  }

  @action.bound
  async loadUsersFromCSV(results: UploadUserType[], context: ImportParseContext): Promise<void> {
    this.importGeneration += 1;
    const generation = this.importGeneration;

    this.usersToUpload = [];
    this.resolveErrors = [];
    this.usersLoaded = false;

    const userRoles: Record<string, string> = {
      user: 'user',
      manager: 'manager',
      'account owner': 'account_owner',
    };

    // license fields are string, and we need to convert them to boolean
    // user can put there any combination of 'True' like 'true', 'TRUE', 'TrUe' etc.
    // so we need to check if it's 'True' or not
    const checkLicense = (license: string | undefined): boolean => license?.toLowerCase() === 'true';

    this.importVocabulary = context.vocabulary;

    const duplicated = duplicateImportColumns(context.fields, context.vocabulary);
    const sites =
      context.vocabulary.showSite && !context.vocabulary.suppressed
        ? await this.lookUpSites(siteNamesInSheet(results))
        : new Map<string, SiteLookupResult>();
    const resolve = createImportRowResolver(context.vocabulary);

    // The file can be removed, or the modal closed, while the lookups are in flight — repopulating here
    // would leave Next enabled over a sheet the user can no longer see.
    if (generation !== this.importGeneration) {
      return;
    }

    runInAction(() => {
      results.forEach((result) => {
        if (isBlankImportRow(result)) {
          return;
        }

        const user: UsersCreationInBulkParams = {
          firstName: result.firstName || null,
          lastName: result.lastName || null,
          email: result.emailAddress || null,
          phoneNumber: result.phoneNumber || null,
          role: result.role ? userRoles[result.role.toLowerCase()] : null,
          dateOfBirth: result.dateOfBirth || null,
          teams: result.teams ? result.teams.split(',').map((i) => i.trim()) : [],
          memorableWord: result.memorableWord || null,
          loneWorkingLicense: checkLicense(result.loneWorkingLicence),
          auditsLicense: checkLicense(result.auditsLicence),
          account: result.account,
          eventsLicense: checkLicense(result.eventsLicence),
          documentsLicense: checkLicense(result.documentsLicence),
          costCentre: result.costCentre || null,
          alarmResponseProfile: result.alarmResponseProfile || null,
        };

        const resolved = resolve(result, sites);
        const error = duplicated.length
          ? `The sheet has more than one column for ${duplicated.join(', ')}.`
          : resolved.error;

        if (error) {
          this.resolveErrors.push({ ...user, source: result, error });
        } else {
          this.usersToUpload.push({ payload: { ...user, ...resolved.fields }, source: result });
        }
      });
    });

    // Blank rows are skipped, so a sheet of nothing but them would otherwise enable Next and then
    // report a successful import of zero users.
    if (!this.usersToUpload.length && !this.resolveErrors.length) {
      this.store.notification.enqueueErrorSnackbar('The file has no user rows to import.');
      return;
    }

    // change it after 2 second, because the progress bar from 'react-papaparse' has a delay
    setTimeout(
      () =>
        runInAction(() => {
          if (generation === this.importGeneration) {
            this.usersLoaded = true;
          }
        }),
      2000
    );
  }

  @action.bound
  async createUsersInBatches(batchSize: number = 1): Promise<void> {
    this.uploadErrors = [...this.resolveErrors];
    const users = [...this.usersToUpload];
    while (users.length) {
      const batch = users.splice(0, batchSize);
      await Promise.all(
        batch.map(async ({ payload, source }) => {
          try {
            await this.api.user.createUserInBulk(payload)();
            this.usersUploadedSuccessfully += 1;
          } catch (e) {
            const error = e as AxiosError;
            this.uploadErrors.push({
              ...payload,
              source,
              error: apiErrorsWithKeys(error, 'Error creating user'),
            });
          }
          this.uploadCount += 1;
        })
      );
    }
    if (
      this.usersToUpload.length > 0 &&
      this.uploadErrors.length === 0 &&
      this.usersToUpload.length === this.usersUploadedSuccessfully
    ) {
      this.uploadedAllUsers = true;
    }
  }

  @action.bound
  clearUsersToUpload(): void {
    this.importGeneration += 1;
    this.usersToUpload = [];
    this.uploadCount = 0;
    this.uploadErrors = [];
    this.resolveErrors = [];
    this.usersUploadedSuccessfully = 0;
    this.uploadedAllUsers = false;
    this.usersLoaded = false;
  }
}
